import React, {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { Redirect, Route, matchPath, useLocation } from 'react-router-dom';

import { addClearPageCacheListener } from '../../../commons/util/pageCache';
import RouteErrorBoundary from './RouteErrorBoundary';

const getLocationKey = location =>
	`${location.pathname || ''}${location.search || ''}`;

const getRouteId = route =>
	[
		Array.isArray(route.path) ? route.path.join('|') : route.path || '__route__',
		route.exact ? 'exact' : '',
		route.redirectTo ? 'redirect' : '',
		route.id || ''
	].join(':');

const getCacheKey = (route, location) => {
	if (route.getCacheKey) {
		return route.getCacheKey(location);
	}
	return getRouteId(route);
};

const getMatch = (route, location) =>
	matchPath(location.pathname, {
		path:      route.path,
		exact:     route.exact,
		strict:    route.strict,
		sensitive: route.sensitive
	});

const findMatchedRoute = (routes, location) => {
	for (let i = 0; i < routes.length; i += 1) {
		const route = routes[i];
		const match = getMatch(route, location);
		if (match) {
			return {
				route,
				match,
				key:      getCacheKey(route, location),
				routeId:  getRouteId(route),
				location
			};
		}
	}
	return null;
};

const renderRouteContent = (route, routeProps) => {
	if (route.redirectTo) {
		return <Redirect to={route.redirectTo} />;
	}
	if (route.render) {
		return route.render(routeProps);
	}
	if (route.component) {
		const Component = route.component;
		return <Component {...routeProps} />;
	}
	if (route.children) {
		return typeof route.children === 'function'
			? route.children(routeProps)
			: route.children;
	}
	return null;
};

const CachedRouteEntry = ({ entry, active }) => (
	<div
		aria-hidden={active ? undefined : true}
		className={`cg-cached-route${active ? ' cg-cached-route--active' : ''}`}
		style={active ? undefined : { display: 'none' }}
	>
		<RouteErrorBoundary resetKey={getLocationKey(entry.location)}>
			<Route
				exact={entry.route.exact}
				location={entry.location}
				path={entry.route.path}
				render={routeProps => renderRouteContent(entry.route, routeProps)}
				sensitive={entry.route.sensitive}
				strict={entry.route.strict}
			/>
		</RouteErrorBoundary>
	</div>
);

const CachedRouteSwitch = ({ routes }) => {
	const location = useLocation();
	const matched = useMemo(() => findMatchedRoute(routes, location), [
		routes,
		location
	]);
	const [entries, setEntries] = useState(() => {
		if (matched && matched.route.cache !== false && !matched.route.redirectTo) {
			return [{ ...matched, generation: 0 }];
		}
		return [];
	});
	const [cacheGeneration, setCacheGeneration] = useState(0);
	const scrollPositionsRef = useRef({});
	const lastLocationKeyRef = useRef(getLocationKey(location));

	const matchedEntry = useMemo(() => {
		if (!matched) {
			return null;
		}
		return { ...matched, generation: cacheGeneration };
	}, [matched, cacheGeneration]);

	useEffect(() => addClearPageCacheListener(() => {
		setEntries([]);
		setCacheGeneration(generation => generation + 1);
		scrollPositionsRef.current = {};
	}), []);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.history) {
			return undefined;
		}
		const previous = window.history.scrollRestoration;
		window.history.scrollRestoration = 'manual';
		return () => {
			window.history.scrollRestoration = previous;
		};
	}, []);

	useLayoutEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}

		const previousKey = lastLocationKeyRef.current;
		scrollPositionsRef.current[previousKey] = {
			x: window.pageXOffset || 0,
			y: window.pageYOffset || 0
		};

		const currentKey = getLocationKey(location);
		lastLocationKeyRef.current = currentKey;
		const saved = scrollPositionsRef.current[currentKey] || { x: 0, y: 0 };
		const restore = () => window.scrollTo(saved.x, saved.y);

		const frame =
			typeof window.requestAnimationFrame === 'function'
				? window.requestAnimationFrame(restore)
				: window.setTimeout(restore, 0);

		return () => {
			if (typeof window.cancelAnimationFrame === 'function') {
				window.cancelAnimationFrame(frame);
			} else {
				window.clearTimeout(frame);
			}
		};
	}, [location]);

	useEffect(() => {
		if (!matchedEntry || matchedEntry.route.cache === false || matchedEntry.route.redirectTo) {
			return;
		}
		setEntries(current => {
			const activeEntries = current.filter(
				entry => entry.generation === cacheGeneration
			);
			const currentLocationKey = getLocationKey(matchedEntry.location);
			const existing = activeEntries.find(entry => entry.key === matchedEntry.key);
			if (existing) {
				if (
					getLocationKey(existing.location) === currentLocationKey &&
					existing.routeId === matchedEntry.routeId
				) {
					return current;
				}
				return activeEntries.map(entry => {
					if (entry.key === matchedEntry.key) {
						return {
							...entry,
							location: matchedEntry.location,
							match:    matchedEntry.match
						};
					}
					return entry;
				});
			}
			return [...activeEntries, matchedEntry];
		});
	}, [cacheGeneration, matchedEntry]);

	const entriesToRender = useMemo(() => {
		const activeEntries = entries.filter(
			entry => entry.generation === cacheGeneration
		);
		if (!matchedEntry || matchedEntry.route.cache === false || matchedEntry.route.redirectTo) {
			return activeEntries;
		}
		const exists = activeEntries.some(entry => entry.key === matchedEntry.key);
		if (exists) {
			return activeEntries.map(entry => {
				if (entry.key === matchedEntry.key) {
					return {
						...entry,
						location: matchedEntry.location,
						match:    matchedEntry.match
					};
				}
				return entry;
			});
		}
		return [...activeEntries, matchedEntry];
	}, [cacheGeneration, entries, matchedEntry]);

	const activeKey =
		matchedEntry && matchedEntry.route.cache !== false && !matchedEntry.route.redirectTo
			? matchedEntry.key
			: null;

	return (
		<>
			{entriesToRender.map(entry => (
				<CachedRouteEntry
					key={`${entry.generation}:${entry.key}`}
					active={entry.key === activeKey}
					entry={entry}
				/>
			))}
			{matched && (!activeKey) && (
				<RouteErrorBoundary resetKey={getLocationKey(location)}>
					<Route
						exact={matched.route.exact}
						path={matched.route.path}
						render={routeProps => renderRouteContent(matched.route, routeProps)}
						sensitive={matched.route.sensitive}
						strict={matched.route.strict}
					/>
				</RouteErrorBoundary>
			)}
		</>
	);
};

export default CachedRouteSwitch;
